/**
 * The generated client, wrapped in the two things a browser needs and the
 * generator does not know about: the CSRF header, and the difference between
 * "the server said no" and "there is no server".
 *
 * Nothing here re-declares a request shape. `createClient` comes from
 * `@davinci/contracts`, whose source is the JSON Schemas — issue #5 says "the
 * **generated** client from P0.2 (not hand-written fetches)", and the reason
 * is the one P0.2 exists for: a hand-written fetch is a second source of truth
 * that drifts silently, in the direction where the client keeps compiling
 * against a shape the server stopped returning.
 *
 * What this file adds is a `fetch` implementation, which is the extension
 * point the generated client already offers.
 */

import { ApiRequestError, createClient, readErrorEnvelope, type Client } from '@davinci/contracts';

/**
 * Re-exported, not redefined.
 *
 * This file used to declare its own `ApiRequestFailed` with its own envelope
 * parser beside it, byte-identical to the generated one. Two classes for one
 * concept meant `instanceof` answered differently depending on which layer
 * happened to throw, and two parsers meant a constraint added to the schema
 * could be honoured by one and not the other. Both are the drift P0.2 exists
 * to prevent, arriving inside the code that prevents it.
 */
export { ApiRequestError } from '@davinci/contracts';

/** BACKEND_DESIGN §12: "All routes are under `/api/v1`." */
export const API_BASE_URL = '/api/v1';

/** The header the server requires on every mutation (session.ts, §14.2). */
const CSRF_HEADER = 'x-davinci-csrf';

/** Set by `GET /launch`, script-readable on purpose so this can read it. */
const CSRF_COOKIE = 'davinci_csrf';

/**
 * The same term of art the server uses. A method that does not change state
 * needs no CSRF token, and asking for one on `GET` would mean the app could
 * not read anything before its first mutation.
 */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/**
 * The server is not reachable at all — the process is not running, or it
 * stopped mid-session.
 *
 * FRONTEND_DESIGN §6.4: "On a network/process failure, say 'DaVinci is not
 * available locally' and offer retry; do not label the answer wrong." That
 * last clause is why this is a distinct type rather than a generic error. A
 * failed request during an attempt must never be presented as a failed
 * *answer*: that would be inference manufactured from a network fault, which
 * is invariant 2's whole concern arriving through the front door.
 */
export class LocalServerUnavailable extends Error {
  // Written as a field assignment rather than a constructor parameter
  // property: `erasableSyntaxOnly` is on repo-wide, so every type annotation
  // has to erase to nothing, and a parameter property emits an assignment.
  readonly reason: unknown;

  constructor(reason: unknown) {
    super('DaVinci is not available locally.');
    this.name = 'LocalServerUnavailable';
    this.reason = reason;
  }
}


/** Reads a cookie by name. Returns `undefined` rather than an empty string. */
export function readCookie(cookieHeader: string, name: string): string | undefined {
  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    const value = part.slice(separator + 1).trim();
    if (value === '') return undefined;
    try {
      return decodeURIComponent(value);
    } catch {
      // A stray `%` makes `decodeURIComponent` throw `URIError`. Our own token
      // is base64url, so encoding is a no-op and this cannot happen to a
      // cookie this server set — but cookies are not port-isolated, so any
      // other process on 127.0.0.1 can set `davinci_csrf` for this host. An
      // uncaught throw here would escape the wrapper's own try block and land
      // in the generic "something went wrong" branch, bypassing the very
      // classification this file exists to provide.
      return value;
    }
  }
  return undefined;
}

/**
 * Whether a request URL stays on this origin.
 *
 * The CSRF token is attached by method alone unless something checks this. Two
 * ways it could leave: a `baseUrl` pointing elsewhere, and a `307`/`308`
 * redirect on a mutation — the fetch spec strips `Cookie` and `Authorization`
 * across origins but *keeps* custom headers like ours. Neither is reachable
 * today (the shipped `baseUrl` is relative, the CSP sends `connect-src 'self'`,
 * and no route redirects on a mutation), which is exactly when a check is
 * cheap to add. A relative URL is same-origin by construction.
 */
function isSameOrigin(url: string): boolean {
  const origin = globalThis.location?.origin;
  if (origin === undefined) return !/^[a-z][a-z0-9+.-]*:/i.test(url);
  try {
    return new URL(url, origin).origin === origin;
  } catch {
    return false;
  }
}

export interface DavinciFetchOptions {
  readonly fetch: typeof fetch;
  /** Injected so tests need no document. Defaults to `document.cookie`. */
  readonly readCookieHeader?: () => string;
  /**
   * Called once per response that is a `401` with the contract's
   * `unauthenticated` code (#371) - the server saying "this request carries no
   * live session". The browser entry point sends the tab to `/login`; tests
   * pass a spy. It is called BEFORE the error is thrown and does not replace
   * it, so a screen that is still mounted keeps its own failure rendering.
   *
   * Only that code. `invalid_credentials` (a wrong password) is also a 401 and
   * must not redirect: the person is already on the login page.
   */
  readonly onUnauthenticated?: () => void;
}

/**
 * A `fetch` that attaches the CSRF token to mutations and classifies failures.
 *
 * Three deliberate choices:
 *
 * - **`credentials: 'same-origin'`.** The session cookie is `HttpOnly` and
 *   `SameSite=Strict`; the frontend is served by the same process on the same
 *   origin, so this is the browser default — stated anyway, because a future
 *   `'omit'` would produce a wall of 401s that looks like an auth bug.
 * - **The token is read per request, not captured once.** `GET /launch` sets
 *   it, and a page that loaded before the exchange would otherwise cache
 *   `undefined` forever.
 * - **A rejected `fetch` becomes `LocalServerUnavailable`; a non-2xx becomes
 *   `ApiRequestError`.** These are genuinely different situations and the UI
 *   owes the founder different sentences for them.
 */
export function createDavinciFetch(options: DavinciFetchOptions): typeof fetch {
  const readCookieHeader = options.readCookieHeader ?? (() => document.cookie);

  return async function davinciFetch(input, init) {
    const method = (init?.method ?? 'GET').toUpperCase();
    const headers = new Headers(init?.headers);
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    if (!SAFE_METHODS.has(method) && isSameOrigin(url)) {
      const token = readCookie(readCookieHeader(), CSRF_COOKIE);
      if (token !== undefined) headers.set(CSRF_HEADER, token);
    }

    let response: Response;
    try {
      response = await options.fetch(input, {
        ...init,
        headers,
        credentials: 'same-origin',
        // `error` rather than the `follow` default. A mutation is the one
        // request where a redirect could carry our custom CSRF header to
        // another origin, and this app has no route that legitimately
        // redirects an API call — so following one is never the right answer.
        redirect: 'error',
      });
    } catch (error) {
      throw new LocalServerUnavailable(error);
    }

    if (!response.ok) {
      // `readErrorEnvelope` is called outside the try above, and that is only
      // safe because it is documented never to reject. A version that could
      // throw would escape into the caller's generic "something went wrong"
      // branch, bypassing the classification this whole function exists for.
      const envelope = await readErrorEnvelope(response);
      if (response.status === 401 && envelope?.code === 'unauthenticated') {
        options.onUnauthenticated?.();
      }
      throw new ApiRequestError(response.status, envelope);
    }
    return response;
  };
}

export interface CreateApiOptions {
  readonly baseUrl?: string;
  readonly fetch?: typeof fetch;
  readonly readCookieHeader?: () => string;
  /** See `DavinciFetchOptions.onUnauthenticated`. */
  readonly onUnauthenticated?: () => void;
}

/** Where a tab with no session is sent (#371). */
export const LOGIN_PATH = '/login';

/**
 * The browser's answer to "this request has no live session": go to the login
 * page. A hard navigation on purpose - it also discards every in-memory query
 * result, so nothing fetched for the previous session (or the previous account)
 * can still be on screen or in the cache once someone else signs in on this tab.
 * Does nothing on the login and sign-up pages themselves: a 401 there would
 * only reload the page the person is already typing into.
 */
export function redirectToLogin(
  location: Pick<Location, 'pathname' | 'assign'> = globalThis.location,
): void {
  if (location.pathname === LOGIN_PATH || location.pathname === '/signup') return;
  location.assign(LOGIN_PATH);
}

export function createApi(options: CreateApiOptions = {}): Client {
  return createClient({
    baseUrl: options.baseUrl ?? API_BASE_URL,
    fetch: createDavinciFetch({
      fetch: options.fetch ?? globalThis.fetch.bind(globalThis),
      ...(options.readCookieHeader !== undefined
        ? { readCookieHeader: options.readCookieHeader }
        : {}),
      ...(options.onUnauthenticated !== undefined
        ? { onUnauthenticated: options.onUnauthenticated }
        : {}),
    }),
  });
}
